<template>
	<div class="captcha">
		<cap-widget ref="captcha" hidden :data-cap-api-endpoint="captchaApiEndpoint"
			@solve="handleSolve" @reset="handleReset" @error="handleError"></cap-widget>
		<button v-if="state === 'default'" type="button" class="captcha__start"
			:disabled="disabled" @click="verify">
			<v-icon size="24" color="primary">mdi-checkbox-blank-outline</v-icon>
			<span class="captcha__text">
				<span>Я человек</span>
				<span class="captcha__hint">Подтвердите, чтобы продолжить</span>
			</span>
		</button>
		<div v-else class="captcha__content" role="status">
			<template v-if="state === 'error'">
				<v-icon size="20" color="error">mdi-alert-circle</v-icon>
				<span class="captcha__text">Не удалось выполнить проверку</span>
				<button type="button" class="captcha__retry" :disabled="disabled" @click="verify">Повторить</button>
			</template>
			<template v-else>
				<v-progress-circular v-if="state === 'loading'" indeterminate :size="24" :width="2" color="primary"></v-progress-circular>
				<v-icon v-else size="24" color="primary">mdi-checkbox-marked</v-icon>
				<span class="captcha__text">
					<span>{{ state === 'loading' ? 'Проверяем…' : 'Проверка пройдена' }}</span>
					<span class="captcha__hint">{{ state === 'loading' ? 'Это займёт несколько секунд' : 'Можно продолжить вход' }}</span>
				</span>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";
import "cap-widget";
import type { CapSolveEvent, CapWidget } from "cap-widget";
import { captchaApiEndpoint } from "@rmp/core/services/captcha";

const props = withDefaults(defineProps<{ value: string; disabled?: boolean }>(), {
	disabled: false
});
const emit = defineEmits<{ (event: "input", value: string): void }>();
const captcha = ref<CapWidget | null>(null);
const state = ref<"default" | "loading" | "success" | "error">("default");
let disposed = false;

const handleReset = () => {
	if(disposed) return;
	state.value = "default";
	emit("input", "");
};

const handleError = () => {
	if(disposed) return;
	state.value = "error";
	emit("input", "");
};

const handleSolve = (event: CapSolveEvent) => {
	if(disposed) return;
	if(props.disabled) {
		captcha.value?.reset();
		return;
	}
	state.value = "success";
	emit("input", event.detail.token);
};

const verify = async () => {
	if(props.disabled || state.value === "loading" || !captcha.value) return;
	state.value = "loading";
	try {
		const result = await captcha.value.solve();
		if(!disposed && (!result?.success || !result.token)) handleError();
	} catch {
		handleError();
	}
};

watch(() => props.value, value => {
	if(!value && captcha.value?.token) captcha.value.reset();
});

onBeforeUnmount(() => {
	disposed = true;
	emit("input", "");
});
</script>

<style scoped lang="scss">
.captcha {
	width: 100%;
	min-height: 80px;
	border: 1px solid var(--v-neutral-lighten3);
	border-radius: 8px;
	background: var(--v-white-base);
	color: var(--v-neutral-darken3);
	font-size: 16px;
	line-height: 24px;

	&__start, &__content {
		display: flex;
		align-items: center;
		gap: 16px;
		width: 100%;
		min-height: 78px;
		padding: 16px;
		text-align: left;
	}

	&__start, &__retry {
		&:focus-visible {
			outline: 2px solid var(--v-primary-base);
			outline-offset: 2px;
		}

		&:disabled {
			cursor: default;
			opacity: 0.6;
		}
	}

	&__start {
		border-radius: inherit;
	}

	&__text {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 4px;
		min-width: 0;
	}

	&__hint {
		color: var(--v-grey-darken1);
		font-size: 12px;
		line-height: 16px;
	}

	&__retry {
		flex-shrink: 0;
		font-size: 14px;
		font-weight: 600;
	}
}
</style>
